import type {
  CalendarProps as AriaCalendarProps,
  DateValue,
} from "react-aria-components";

import { isSameDay, today } from "@internationalized/date";
import { Timezone } from "@nlc/shared-types";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { CalendarCell, CalendarGrid, Heading } from "react-aria-components";
import { Button } from "react-aria-components";
import {
  Calendar as AriaCalendar,
  CalendarGridHeader as AriaCalendarGridHeader,
  CalendarGridBody,
  CalendarHeaderCell,
  Text,
  useLocale,
} from "react-aria-components";
import { tv } from "tailwind-variants";

const cellStyles = tv({
  base: "flex size-9 cursor-default items-center justify-center rounded-full text-sm forced-color-adjust-none",
  compoundVariants: [
    {
      className: "ring-white",
      isSelected: true,
      isToday: true,
    },
  ],
  variants: {
    isDisabled: {
      true: "text-sm font-normal text-text-primary",
    },
    isSelected: {
      false:
        "text-sm font-normal text-text-secondary hover:bg-bg-primary-hover hover:text-sm hover:font-medium hover:text-text-secondary-hover disabled:text-text-disabled",
      true: " bg-bg-brand-solid text-sm font-medium text-text-white  outline-none",
    },
    isToday: {
      true: "ring-1 ring-bg-brand-solid font-medium",
    },
  },
});

export interface CalendarProps<T extends DateValue>
  extends Omit<AriaCalendarProps<T>, "visibleDuration"> {
  errorMessage?: string;
  timezone?: Timezone;
}

export function Calendar<T extends DateValue>({
  errorMessage,
  timezone = Timezone.EST,
  ...props
}: CalendarProps<T>) {
  return (
    <AriaCalendar {...props}>
      <CalendarHeader />
      <CalendarGrid>
        <CalendarGridHeader />
        <CalendarGridBody>
          {(date) => (
            <CalendarCell
              className={({ isDisabled, isSelected }) =>
                cellStyles({
                  isDisabled,
                  isSelected,
                  isToday: isSameDay(date, today(timezone) as typeof date),
                })
              }
              date={date}
            />
          )}
        </CalendarGridBody>
      </CalendarGrid>
      {errorMessage && (
        <Text className="text-sm" slot="errorMessage">
          {errorMessage}
        </Text>
      )}
    </AriaCalendar>
  );
}

export function CalendarHeader() {
  const { direction } = useLocale();

  return (
    <header className="flex w-full items-center gap-1 px-1 pb-4">
      <Button slot="previous">
        {direction === "rtl" ? (
          <ChevronRight aria-hidden className="text-button-secondary-fg" />
        ) : (
          <ChevronLeft aria-hidden className="text-button-secondary-fg" />
        )}
      </Button>
      <Heading className="mx-2 flex-1 text-center text-md font-semibold text-text-secondary" />
      <Button slot="next">
        {direction === "rtl" ? (
          <ChevronLeft aria-hidden className="text-button-secondary-fg" />
        ) : (
          <ChevronRight aria-hidden className="text-button-secondary-fg" />
        )}
      </Button>
    </header>
  );
}

export function CalendarGridHeader() {
  return (
    <AriaCalendarGridHeader>
      {(day) => (
        <CalendarHeaderCell className="py-4 text-sm font-medium text-text-secondary">
          {day}
        </CalendarHeaderCell>
      )}
    </AriaCalendarGridHeader>
  );
}
